import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, Eye } from 'lucide-react';
import {
  Button,
  Card,
  Field,
  Loading,
  Notice,
  PageHeader,
  StatusBadge,
} from '../components/ui';
import {
  api,
  authenticatedPostBlobUrl,
  downloadAuthenticated,
  jsonBody,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCatalogs } from '../lib/hooks';
import {
  RichClinicalEditor,
  type RichClinicalEditorHandle,
} from '../components/RichClinicalEditor';
import { Cie10Combobox } from '../components/Cie10Combobox';
import type {
  ClinicalReportTemplate,
  ClinicalTemplatePreference,
  ReportRecord,
  StudyDetail,
} from '../lib/types';

interface ReportDiagnosisForm {
  isPrimary: boolean;
  cie10Code: string;
  description: string;
  diagnosisType: string;
  classification: string;
  registeredAt: string;
}

const blankReport = {
  textReport: '',
  richContentText: '',
  richContentJson: null as { type: 'doc'; content: unknown[] } | null,
  selectedTemplateId: '' as string,
  findings: '',
  conclusionDiagnosis: '',
  recommendations: '',
  chiefComplaint: '',
  currentIllness: '',
  physicalExam: '',
  clinicalImpression: '',
  managementPlan: '',
  careModality: '',
  diagnoses: [] as ReportDiagnosisForm[],
  internalObservations: '',
  medicalDecision: '',
  professionalId: '',
};

const emptyDocumentHtml = '<p><br></p>';
const autosaveDelayMs = 1800;

type AutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function plainTextToHtml(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return emptyDocumentHtml;
  return `<p>${escapeHtml(trimmed).replaceAll('\n', '<br>')}</p>`;
}

function htmlToPlainText(html: string) {
  const element = document.createElement('div');
  element.innerHTML = html;
  return (element.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

function normalizedTemplateSearch(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9@.-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function templateCaseWarnings(html: string, study: StudyDetail) {
  const text = normalizedTemplateSearch(`${htmlToPlainText(html)} ${html}`);
  const warnings: string[] = [];
  if (/<img\b/i.test(html) || /data:image\//i.test(html)) warnings.push('imágenes clínicas');
  const identifiers = [
    ['nombre del paciente', study.patient.fullName],
    ['documento del paciente', study.patient.identificationNumber],
    ['código del paciente', study.patientCode],
    ['código del estudio', study.code],
    ['código Profusion', study.profusionPatientCode],
    ['código Profusion', study.profusionStudyCode],
    ['correo del paciente', study.patient.email],
    ['teléfono del paciente', study.patient.primaryPhone],
  ] as const;
  for (const [label, value] of identifiers) {
    const candidate = normalizedTemplateSearch(value ?? '');
    if (candidate.length >= 5 && text.includes(candidate)) warnings.push(label);
  }
  return [...new Set(warnings)];
}

function richContentHtml(
  content: { type: 'doc'; content: unknown[] } | null | undefined,
) {
  const html = content?.content.find(
    (item): item is { html: string } =>
      typeof item === 'object' &&
      item !== null &&
      'html' in item &&
      typeof (item as { html?: unknown }).html === 'string',
  )?.html;
  return html?.trim() || emptyDocumentHtml;
}

function buildReportPayload(form: typeof blankReport, html: string) {
  const plainText = htmlToPlainText(html);
  return {
    ...form,
    textReport: plainText,
    richContentJson: {
      type: 'doc' as const,
      content: [{ html }],
    },
    richContentText: plainText,
    selectedTemplateId: form.selectedTemplateId
      ? Number(form.selectedTemplateId)
      : null,
    professionalId: Number(form.professionalId),
    findings: form.findings || null,
    conclusionDiagnosis: form.conclusionDiagnosis || plainText,
    recommendations: form.recommendations || null,
    chiefComplaint: form.chiefComplaint || null,
    currentIllness: form.currentIllness || null,
    physicalExam: form.physicalExam || null,
    clinicalImpression: form.clinicalImpression || null,
    managementPlan: form.managementPlan || null,
    careModality: form.careModality || null,
    diagnoses: form.diagnoses.map((diagnosis) => ({
      ...diagnosis,
      diagnosisType: diagnosis.diagnosisType || null,
      classification: diagnosis.classification || null,
      registeredAt: diagnosis.registeredAt
        ? new Date(`${diagnosis.registeredAt}T12:00:00.000Z`)
        : null,
    })),
    internalObservations: form.internalObservations || null,
    medicalDecision: form.medicalDecision || null,
  };
}

function displayValue(value: unknown, fallback = 'Sin registrar') {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function ReportPage() {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const client = useQueryClient();
  const catalogs = useCatalogs();
  const { user } = useAuth();
  const study = useQuery({
    queryKey: ['study', id],
    queryFn: () => api<StudyDetail>(`/api/studies/${id}`),
  });
  const report = useQuery({
    queryKey: ['report', id],
    queryFn: () => api<ReportRecord | null>(`/api/studies/${id}/report`),
  });
  const templates = useQuery({
    queryKey: ['clinical-report-templates'],
    enabled: Boolean(study.data),
    queryFn: () =>
      api<ClinicalReportTemplate[]>('/api/clinical-report-templates'),
  });
  const templatePreference = useQuery({
    queryKey: ['clinical-report-template-preference', study.data?.studyType.id],
    enabled: Boolean(study.data),
    queryFn: () =>
      api<ClinicalTemplatePreference | null>(
        `/api/clinical-report-template-preference?studyTypeId=${study.data!.studyType.id}`,
      ),
  });
  const [form, setForm] = useState({ ...blankReport });
  const [editorHtml, setEditorHtml] = useState(emptyDocumentHtml);
  const [templateName, setTemplateName] = useState('');
  const [templateStudyTypeId, setTemplateStudyTypeId] = useState('');
  const [templateKind, setTemplateKind] = useState<'PLANTILLA_COMPLETA' | 'BLOQUE_CLINICO'>('PLANTILLA_COMPLETA');
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [returnComment, setReturnComment] = useState('');
  const [technicalReturnComment, setTechnicalReturnComment] = useState('');
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const editorRef = useRef<RichClinicalEditorHandle | null>(null);
  const lastSavedSignatureRef = useRef('');
  const autosaveTimerRef = useRef<number | null>(null);
  const hasIncompleteDiagnosis = form.diagnoses.some(
    (diagnosis) =>
      !diagnosis.cie10Code.trim() || !diagnosis.description.trim(),
  );
  const skipNextReportHydrationRef = useRef(false);
  const skipNextAutosaveEvaluationRef = useRef(false);
  const orderedTemplates = useMemo(() => {
    const studyTypeId = study.data?.studyType.id;
    const rank = (template: ClinicalReportTemplate) =>
      template.studyTypeId === studyTypeId ? 0 : template.studyTypeId === null ? 1 : 2;
    return [...(templates.data ?? [])].sort(
      (left, right) =>
        rank(left) - rank(right) ||
        left.displayOrder - right.displayOrder ||
        left.name.localeCompare(right.name, 'es'),
    );
  }, [study.data?.studyType.id, templates.data]);
  const completeTemplates = orderedTemplates.filter(
    (template) => template.contentKind !== 'BLOQUE_CLINICO',
  );
  const clinicalBlocks = orderedTemplates.filter(
    (template) => template.contentKind === 'BLOQUE_CLINICO',
  );

  const readEditorHtml = () =>
    editorRef.current?.getHTML() || editorHtml || emptyDocumentHtml;

  useEffect(() => {
    if (skipNextReportHydrationRef.current) {
      skipNextReportHydrationRef.current = false;
      return;
    }
    const current = report.data;
    const currentStudy = study.data;
    if (current) {
      skipNextAutosaveEvaluationRef.current = true;
      const loadedEditorHtml = current.richContentJson
        ? richContentHtml(current.richContentJson)
        : plainTextToHtml(current.richContentText ?? current.textReport);
      const loadedForm = {
        textReport: current.textReport,
        richContentText: current.richContentText ?? current.textReport,
        richContentJson: current.richContentJson,
        selectedTemplateId: current.selectedTemplateId
          ? String(current.selectedTemplateId)
          : '',
        findings: current.findings ?? '',
        conclusionDiagnosis: current.conclusionDiagnosis,
        recommendations: current.recommendations ?? '',
        chiefComplaint: current.chiefComplaint ?? '',
        currentIllness: current.currentIllness ?? '',
        physicalExam: current.physicalExam ?? '',
        clinicalImpression: current.clinicalImpression ?? '',
        managementPlan: current.managementPlan ?? '',
        careModality: current.careModality ?? '',
        diagnoses: (current.diagnoses ?? []).map((diagnosis) => ({
          isPrimary: diagnosis.isPrimary,
          cie10Code: diagnosis.cie10Code,
          description: diagnosis.description,
          diagnosisType: diagnosis.diagnosisType ?? '',
          classification: diagnosis.classification ?? '',
          registeredAt: diagnosis.registeredAt?.slice(0, 10) ?? '',
        })),
        internalObservations: current.internalObservations ?? '',
        medicalDecision: current.medicalDecision ?? '',
        professionalId: String(current.professionalId),
      };
      setEditorHtml(loadedEditorHtml);
       editorRef.current?.setContent(loadedEditorHtml);
      setForm(loadedForm);
      lastSavedSignatureRef.current = JSON.stringify(
        buildReportPayload(loadedForm, loadedEditorHtml),
      );
      setAutosaveStatus('saved');
    } else if (currentStudy) {
      skipNextAutosaveEvaluationRef.current = true;
      setEditorHtml(emptyDocumentHtml);
       editorRef.current?.setContent(emptyDocumentHtml);
      setForm((value) => {
        const emptyForm = {
        ...value,
        textReport: '',
        richContentText: '',
        richContentJson: null,
        selectedTemplateId: '',
        professionalId: String(currentStudy.doctor.id),
        chiefComplaint: value.chiefComplaint || currentStudy.reason,
        };
        lastSavedSignatureRef.current = JSON.stringify(
          buildReportPayload(emptyForm, emptyDocumentHtml),
        );
        return emptyForm;
      });
      setAutosaveStatus('idle');
    }
  }, [report.data, study.data]);

  const reportPayload = () => buildReportPayload(form, readEditorHtml());

  const saveDraft = useMutation({
    mutationFn: ({ payload }: { payload: ReturnType<typeof buildReportPayload>; signature: string; automatic: boolean }) =>
      api<ReportRecord>(`/api/studies/${id}/report`, {
        method: 'PUT',
        ...jsonBody(payload),
      }),
    onMutate: ({ automatic }) => {
      if (automatic) setAutosaveStatus('saving');
    },
    onSuccess: (saved, request) => {
      lastSavedSignatureRef.current = request.signature;
      setLastSavedAt(new Date());
      if (!request.automatic) {
        skipNextReportHydrationRef.current = true;
        client.setQueryData(['report', id], saved);
      }
      const currentSignature = JSON.stringify(reportPayload());
      setAutosaveStatus(
        currentSignature === request.signature ? 'saved' : 'dirty',
      );
      if (!request.automatic) setMessage('Borrador médico guardado.');
    },
    onError: (error, request) => {
      setAutosaveStatus('error');
      if (request.automatic) return;
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible guardar el informe.',
      );
    },
  });

  const requestDraftSave = (automatic: boolean) => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const payload = reportPayload();
    if (
      payload.diagnoses.some(
        (diagnosis) =>
          !diagnosis.cie10Code.trim() || !diagnosis.description.trim(),
      )
    ) {
      setAutosaveStatus('dirty');
      if (!automatic) {
        setMessage(
          'Seleccione un diagnóstico CIE-10 o quite la fila incompleta antes de guardar.',
        );
      }
      return;
    }
    const signature = JSON.stringify(payload);
    saveDraft.mutate({ payload, signature, automatic });
  };

  const previewDraft = useMutation({
    mutationFn: () =>
      authenticatedPostBlobUrl(
        `/api/studies/${id}/report/preview`,
        reportPayload(),
      ),
    onSuccess: ({ url, contentType }) => {
      if (contentType !== 'application/pdf') {
        URL.revokeObjectURL?.(url);
        setPreviewError('La previsualización no produjo un PDF válido.');
        return;
      }
      setPreviewError(null);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL?.(current);
        return url;
      });
    },
    onError: (error) => {
      setPreviewError(
        error instanceof Error
          ? error.message
          : 'No fue posible generar la previsualización.',
      );
    },
  });

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL?.(previewUrl);
    },
    [previewUrl],
  );

  useEffect(() => {
    if (skipNextAutosaveEvaluationRef.current) {
      skipNextAutosaveEvaluationRef.current = false;
      return;
    }
    const currentStudy = study.data;
    const currentReport = report.data;
    const autosaveEnabled = Boolean(
      currentStudy &&
        user?.roles.includes('MEDICO') &&
        user.professionalId === currentStudy.doctor.id &&
        user.email.toLowerCase() === currentStudy.doctorEmail.toLowerCase() &&
        ['INF_EN_ELAB', 'DEV_CORRECCION'].includes(currentStudy.stateCode) &&
        !currentReport?.lockedAt &&
        (!currentReport || ['BORRADOR', 'DEVUELTO'].includes(currentReport.status)),
    );
    if (!autosaveEnabled || saveDraft.isPending) return;

    const payload = reportPayload();
    const signature = JSON.stringify(payload);
    if (
      payload.diagnoses.some(
        (diagnosis) =>
          !diagnosis.cie10Code.trim() || !diagnosis.description.trim(),
      )
    ) {
      setAutosaveStatus('dirty');
      return;
    }
    if (!payload.richContentText.trim()) {
      lastSavedSignatureRef.current = signature;
      setAutosaveStatus('idle');
      return;
    }
    if (signature === lastSavedSignatureRef.current) {
      setAutosaveStatus('saved');
      return;
    }

    setAutosaveStatus('dirty');
    autosaveTimerRef.current = window.setTimeout(
      () => {
        autosaveTimerRef.current = null;
        saveDraft.mutate({ payload, signature, automatic: true });
      },
      autosaveDelayMs,
    );
    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [form, editorHtml, report.data, saveDraft.isPending, study.data, user]);

  useEffect(() => {
    const hasPendingChanges = ['dirty', 'saving', 'error'].includes(
      autosaveStatus,
    );
    if (!hasPendingChanges) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [autosaveStatus]);

  const saveTemplate = useMutation({
    mutationFn: () => {
      const html = readEditorHtml();
      const plainText = htmlToPlainText(html);
      if (!study.data) throw new Error('Estudio no disponible.');
      const warnings = templateCaseWarnings(html, study.data);
      if (warnings.length) {
        throw new Error(`La plantilla contiene información propia del caso (${warnings.join(', ')}). Retírela antes de guardar.`);
      }
      return api('/api/clinical-report-templates', {
        method: 'POST',
        ...jsonBody({
          name: templateName.trim(),
          category: 'Biblioteca clínica',
          studyTypeId: templateStudyTypeId ? Number(templateStudyTypeId) : null,
          sourceStudyId: id,
          contentJson: { type: 'doc', content: [{ html }] },
          contentText: plainText,
          contentKind: templateKind,
          status: 'PUBLICADA',
          createNewVersion: false,
          active: true,
          systemTemplate: false,
          displayOrder: 100,
        }),
      });
    },
    onSuccess: async () => {
      setMessage('Plantilla clínica guardada.');
      setTemplateName('');
      setTemplateStudyTypeId('');
      setTemplateKind('PLANTILLA_COMPLETA');
      await templates.refetch();
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible guardar la plantilla.',
      ),
  });

  const saveTemplatePreference = useMutation({
    mutationFn: (templateId: number) => {
      if (!study.data) throw new Error('Estudio no disponible.');
      return api<ClinicalTemplatePreference>('/api/clinical-report-template-preference', {
        method: 'POST',
        ...jsonBody({ studyTypeId: study.data.studyType.id, templateId }),
      });
    },
    onSuccess: async () => {
      setMessage('Plantilla predeterminada guardada para este tipo de estudio.');
      await templatePreference.refetch();
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible guardar la plantilla predeterminada.',
      ),
  });

  const finalizeReport = useMutation({
    mutationFn: async () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      if (!study.data) throw new Error('Estudio no disponible.');
      let approvalState: Pick<StudyDetail, 'stateCode' | 'rowVersion'> = {
        stateCode: study.data.stateCode,
        rowVersion: study.data.rowVersion,
      };

      if (['INF_EN_ELAB', 'DEV_CORRECCION'].includes(approvalState.stateCode)) {
        await api(`/api/studies/${id}/report`, {
          method: 'PUT',
          ...jsonBody(reportPayload()),
        });
        const submitted = await api<Partial<StudyDetail>>(
          `/api/studies/${id}/transitions`,
          {
            method: 'POST',
            ...jsonBody({
              actionCode:
                approvalState.stateCode === 'DEV_CORRECCION'
                  ? 'REENVIAR'
                  : 'ENVIAR_APROBACION',
              comment: null,
              expectedStateCode: approvalState.stateCode,
              expectedRowVersion: approvalState.rowVersion,
            }),
          },
        );
        if (
          submitted.stateCode !== 'PEND_APROBACION' ||
          submitted.rowVersion == null
        ) {
          throw new Error(
            'El informe quedó guardado, pero no alcanzó la etapa de aprobación. Recargue para continuar.',
          );
        }
        approvalState = {
          stateCode: submitted.stateCode,
          rowVersion: submitted.rowVersion,
        };
      }

      if (approvalState.stateCode !== 'PEND_APROBACION') {
        throw new Error('El informe no está listo para finalizar y aprobar.');
      }

      return api(`/api/studies/${id}/transitions`, {
        method: 'POST',
        ...jsonBody({
          actionCode: 'APROBAR',
          comment: null,
          expectedStateCode: approvalState.stateCode,
          expectedRowVersion: approvalState.rowVersion,
        }),
      });
    },
    onSuccess: async () => {
      setMessage('Informe finalizado y aprobado. Generación documental iniciada.');
      await Promise.all([
        report.refetch(),
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
      ]);
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible finalizar y aprobar el informe.',
      ),
  });

  const decision = useMutation({
    mutationFn: () => {
      if (!study.data) throw new Error('Estudio no disponible.');
      return api(`/api/studies/${id}/transitions`, {
        method: 'POST',
        ...jsonBody({
          actionCode: 'DEVOLVER',
          comment: returnComment.trim(),
          expectedStateCode: study.data.stateCode,
          expectedRowVersion: study.data.rowVersion,
        }),
      });
    },
    onSuccess: async () => {
      setMessage('Informe devuelto; se creó una nueva versión para corrección.');
      setReturnComment('');
      await Promise.all([
        report.refetch(),
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
      ]);
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible registrar la decisión clínica.',
      ),
  });

  const correction = useMutation({
    mutationFn: () =>
      api(`/api/studies/${id}/report/correction`, { method: 'POST' }),
    onSuccess: async () => {
      setMessage('Nueva versión de corrección creada.');
      await report.refetch();
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible crear la versión.',
      ),
  });

  const returnToTechnique = useMutation({
    mutationFn: () => {
      if (!study.data) throw new Error('Estudio no disponible.');
      return api(`/api/studies/${id}/transitions`, {
        method: 'POST',
        ...jsonBody({
          actionCode: 'DEVOLVER_TECNICA',
          comment: technicalReturnComment.trim(),
          expectedStateCode: study.data.stateCode,
          expectedRowVersion: study.data.rowVersion,
        }),
      });
    },
    onSuccess: async () => {
      setMessage('Estudio devuelto a Técnica con la observación registrada.');
      setTechnicalReturnComment('');
      await Promise.all([
        report.refetch(),
        client.invalidateQueries({ queryKey: ['study', id] }),
        client.invalidateQueries({ queryKey: ['studies'] }),
        client.invalidateQueries({ queryKey: ['transitions', id] }),
      ]);
    },
    onError: (error) =>
      setMessage(
        error instanceof Error
          ? error.message
          : 'No fue posible devolver el estudio a Técnica.',
      ),
  });

  const addDiagnosis = () =>
    setForm((current) => ({
      ...current,
      diagnoses: [
        ...current.diagnoses,
        {
          isPrimary: current.diagnoses.length === 0,
          cie10Code: '',
          description: '',
          diagnosisType: '',
          classification: '',
          registeredAt: new Date().toISOString().slice(0, 10),
        },
      ],
    }));
  const updateDiagnosis = <K extends keyof ReportDiagnosisForm>(
    index: number,
    key: K,
    value: ReportDiagnosisForm[K],
  ) => {
    setForm((current) => ({
      ...current,
      diagnoses: current.diagnoses.map((diagnosis, diagnosisIndex) =>
        diagnosisIndex === index
          ? { ...diagnosis, [key]: value }
          : key === 'isPrimary' && value === true
            ? { ...diagnosis, isPrimary: false }
            : diagnosis,
      ),
    }));
  };
  const removeDiagnosis = (index: number) =>
    setForm((current) => ({
      ...current,
      diagnoses: current.diagnoses.filter(
        (_, diagnosisIndex) => diagnosisIndex !== index,
      ),
    }));

  const syncEditor = (html = readEditorHtml()) => {
    const plainText = htmlToPlainText(html);
    setForm((current) => ({
      ...current,
      textReport: plainText,
      richContentText: plainText,
      richContentJson: { type: 'doc', content: [{ html }] },
      conclusionDiagnosis: current.conclusionDiagnosis || plainText,
    }));
  };

  const setEditorContent = (
    html: string,
    templateId: number | null,
    textFallback?: string,
  ) => {
    const plainText = textFallback?.trim() || htmlToPlainText(html);
    setEditorHtml(html);
    editorRef.current?.setContent(html);
    setForm((current) => ({
      ...current,
      textReport: plainText,
      richContentText: plainText,
      richContentJson: { type: 'doc', content: [{ html }] },
      selectedTemplateId: templateId ? String(templateId) : '',
      conclusionDiagnosis: current.conclusionDiagnosis || plainText,
    }));
  };

  const applyTemplate = (templateId: string) => {
    const selected = templates.data?.find(
      (template) => template.id === Number(templateId),
    );
    if (!selected) {
      setPendingTemplateId(null);
      setForm((current) => ({ ...current, selectedTemplateId: '' }));
      return;
    }
    const hasContent = Boolean(htmlToPlainText(readEditorHtml()));
    if (hasContent) {
      setPendingTemplateId(selected.id);
      return;
    }
    setPendingTemplateId(null);
    setEditorContent(
      richContentHtml(selected.contentJson),
      selected.id,
      selected.contentText,
    );
  };

  const resolvePendingTemplate = (mode: 'replace' | 'insert' | 'cancel') => {
    const selected = templates.data?.find(
      (template) => template.id === pendingTemplateId,
    );
    if (!selected || mode === 'cancel') {
      setPendingTemplateId(null);
      return;
    }
    const templateHtml = richContentHtml(selected.contentJson);
    if (mode === 'replace') {
      setEditorContent(templateHtml, selected.id, selected.contentText);
    } else {
      const currentHtml = readEditorHtml();
      setEditorContent(`${currentHtml}${templateHtml}`, null);
    }
    setPendingTemplateId(null);
  };

  const insertClinicalBlock = (blockId: string) => {
    const block = clinicalBlocks.find((item) => item.id === Number(blockId));
    if (!block) return;
    editorRef.current?.insertContent(richContentHtml(block.contentJson));
  };

  if (study.isLoading || report.isLoading) return <Loading />;
  if (study.error || !study.data)
    return <Notice kind="error">No fue posible cargar el estudio.</Notice>;

  const current = report.data;
  const assignedDoctor = Boolean(
    user?.roles.includes('MEDICO') &&
      user.professionalId === study.data.doctor.id &&
      user.email.toLowerCase() === study.data.doctorEmail.toLowerCase(),
  );
  const locked = Boolean(current?.lockedAt) || current?.status === 'APROBADO';
  const editableState = ['INF_EN_ELAB', 'DEV_CORRECCION'].includes(
    study.data.stateCode,
  );
  const editable =
    assignedDoctor &&
    editableState &&
    !locked &&
    (!current || ['BORRADOR', 'DEVUELTO'].includes(current.status));
  const canSubmit =
    editable &&
    !hasIncompleteDiagnosis &&
    Boolean(form.richContentText.trim() || form.textReport.trim());
  const pendingDecision =
    study.data.stateCode === 'PEND_APROBACION' &&
    current?.status === 'PENDIENTE_APROBACION';
  const canReturnToTechnique =
    assignedDoctor &&
    ['PEND_REV_MEDICA', 'INF_EN_ELAB'].includes(study.data.stateCode);
  const canCreatePostApprovalCorrection =
    assignedDoctor &&
    locked &&
    ['ENVIADO', 'PEND_FACTURACION', 'FACTURADO', 'CERRADO'].includes(
      study.data.stateCode,
    );
  const capture = study.data.technicalCapture as Record<string, unknown> | null;
  const technicalValues = {
    ...(study.data.dynamicValues ?? {}),
    ...(capture ?? {}),
  };
  const technicalRows = study.data.studyType.fields
    .filter((field) => field.active)
    .map((field) => ({
      label: field.label,
      value: technicalValues[field.code],
    }))
    .filter((row) => {
      if (Array.isArray(row.value)) return row.value.length > 0;
      return row.value !== null && row.value !== undefined && row.value !== '';
    });
  const additionalTechnicalRows = Array.isArray(capture?.additionalFields)
    ? capture.additionalFields.flatMap((item) => {
        if (!item || typeof item !== 'object') return [];
        const title = 'title' in item ? String(item.title).trim() : '';
        const description = 'description' in item ? String(item.description).trim() : '';
        return title && description ? [{ title, description }] : [];
      })
    : [];

  return (
    <>
      <PageHeader
        title={`Informe médico · ${study.data.code}`}
        description="Redacción, versionamiento, decisión médica y bloqueo de la versión aprobada."
        actions={
          <>
            <StatusBadge value={current?.status ?? 'SIN_BORRADOR'} />
            {current && (
              <span className="version-chip">v{current.version}</span>
            )}
          </>
        }
      />
      {message && (
        <Notice
          kind={
            [
              'guardado',
              'creada',
              'enviado',
              'reenviada',
              'aprobado',
              'devuelto',
            ].some((value) => message.includes(value))
              ? 'success'
              : 'error'
          }
          onClose={() => setMessage(null)}
        >
          {message}
        </Notice>
      )}
      {locked && (
        <Notice kind="info">
          La versión aprobada está bloqueada. Cree una versión de corrección
          para realizar cambios.
        </Notice>
      )}
      {current?.returnComment && study.data.stateCode === 'DEV_CORRECCION' && (
        <Notice kind="info">
          Motivo de devolución: {current.returnComment}
        </Notice>
      )}
      {!locked && !editableState && (
        <Notice kind="info">
          El informe no es editable mientras el estudio se encuentra en{' '}
          {study.data.stateCode.replaceAll('_', ' ')}.
        </Notice>
      )}

      <Card title="Contexto del estudio para revisión médica">
        <div className="medical-context-grid">
          <section>
            <h3>Identificación del paciente</h3>
            <dl className="detail-list">
              <dt>Paciente</dt>
              <dd>{study.data.patient.fullName}<small>{study.data.patient.identificationType} · {study.data.patient.identificationNumber}</small></dd>
              <dt>Fecha de nacimiento</dt>
              <dd>{study.data.patient.birthDate ? new Date(study.data.patient.birthDate).toLocaleDateString('es-CO') : 'Sin registrar'}</dd>
              <dt>Sexo</dt>
              <dd>{displayValue(study.data.patient.sex)}</dd>
            </dl>
          </section>
          <section>
            <h3>Información de la atención</h3>
            <dl className="detail-list">
              <dt>Estudio</dt><dd>{study.data.studyTypeName}</dd>
              <dt>Fecha</dt><dd>{new Date(study.data.studyDate).toLocaleString('es-CO')}</dd>
              <dt>Origen</dt><dd>{study.data.origin.replaceAll('_', ' ')}</dd>
              <dt>Institución / sede</dt><dd>{study.data.institutionName || 'Consultorio'}<small>{study.data.serviceLocation}</small></dd>
              <dt>Motivo</dt><dd>{displayValue(study.data.reason)}</dd>
              <dt>Remitente</dt><dd>{displayValue(study.data.referringProfessional)}<small>{study.data.referringInstitution}</small></dd>
            </dl>
          </section>
          <section>
            <h3>Signos vitales y antropometría</h3>
            <dl className="detail-list">
              <dt>Peso</dt><dd>{capture?.weightKg ? `${capture.weightKg} kg` : 'Sin registrar'}</dd>
              <dt>Altura</dt><dd>{capture?.heightCm ? `${capture.heightCm} cm` : 'Sin registrar'}</dd>
              <dt>Presión arterial</dt><dd>{capture?.systolicBloodPressure && capture?.diastolicBloodPressure ? `${capture.systolicBloodPressure}/${capture.diastolicBloodPressure} mmHg` : 'Sin registrar'}</dd>
              <dt>Frecuencia cardiaca</dt><dd>{capture?.heartRateBpm ? `${capture.heartRateBpm} lpm` : 'Sin registrar'}</dd>
              <dt>Frecuencia respiratoria</dt><dd>{capture?.respiratoryRateBpm ? `${capture.respiratoryRateBpm} rpm` : 'Sin registrar'}</dd>
              <dt>Temperatura</dt><dd>{capture?.temperatureC ? `${capture.temperatureC} °C` : 'Sin registrar'}</dd>
            </dl>
          </section>
          <section>
            <h3>Captura técnica</h3>
            <dl className="detail-list">
              <dt>Procedimiento</dt><dd><StatusBadge value={study.data.procedureStatus} /></dd>
              <dt>Condición del paciente</dt><dd>{displayValue(capture?.patientCondition)}</dd>
              <dt>Antecedentes relevantes</dt><dd>{displayValue(capture?.relevantHistory)}</dd>
              <dt>Descripción técnica</dt><dd>{displayValue(capture?.technicalObservations)}</dd>
              <dt>Incidentes</dt><dd>{displayValue(capture?.procedureIncidents, 'Sin incidentes registrados')}</dd>
              <dt>Comentario de entrega</dt><dd>{displayValue(capture?.deliveryComment)}</dd>
              <dt>Profusion</dt><dd>{study.data.profusionStudyCode || 'Sin referencia'}<small>{study.data.profusionPatientCode || 'Sin código de paciente'}</small></dd>
            </dl>
          </section>
          {technicalRows.length > 0 && (
            <section>
              <h3>Datos configurados del estudio</h3>
              <dl className="detail-list">
                {technicalRows.map((row, index) => (
                  <Fragment key={`${row.label}-${index}`}>
                    <dt>{row.label}</dt><dd>{displayValue(row.value)}</dd>
                  </Fragment>
                ))}
              </dl>
            </section>
          )}
          {additionalTechnicalRows.length > 0 && (
            <section>
              <h3>Datos adicionales de Técnica</h3>
              <dl className="detail-list">
                {additionalTechnicalRows.map((row,index)=><Fragment key={`${row.title}-${index}`}><dt>{row.title}</dt><dd>{row.description}</dd></Fragment>)}
              </dl>
            </section>
          )}
          <section className="medical-evidence-section">
            <h3>Evidencia disponible</h3>
            <div className="medical-evidence-list">
              {study.data.documents.map((document) => (
                <div key={document.id}>
                  <span><strong>{document.fileName}</strong><small>{document.type.replaceAll('_', ' ')} · {document.status}</small></span>
                  <Button type="button" variant="secondary" onClick={() => void downloadAuthenticated(`/api/documents/${document.id}/download`, document.fileName)}>
                    <Download size={16} /> Descargar
                  </Button>
                </div>
              ))}
              {!study.data.documents.length && <p className="muted">No hay documentos cargados para este estudio.</p>}
            </div>
            <small>{study.data.liveSessions.length} sesión(es) en vivo registrada(s).</small>
          </section>
        </div>
      </Card>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          requestDraftSave(false);
        }}
      >
        <Card title="Autoría">
          <div className="form-grid two">
            <Field label="Médico responsable" required>
              <select
                disabled={!editable}
                required
                value={form.professionalId}
                onChange={(event) =>
                  setForm({ ...form, professionalId: event.target.value })
                }
              >
                <option value="">Seleccione…</option>
                {catalogs.data?.professionals
                  .filter(
                    (professional) =>
                      professional.active && professional.type === 'MEDICO',
                  )
                  .map((professional) => (
                    <option key={professional.id} value={professional.id}>
                      {professional.fullName} ·{' '}
                      {professional.registrationNumber || 'sin registro'}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Versión">
              <input
                disabled
                value={current ? String(current.version) : 'Nueva'}
              />
            </Field>
          </div>
        </Card>
        <Card title="Editor del informe">
          <div className="stack">
            {editable && (
              <div className={`autosave-status autosave-${autosaveStatus}`} role="status" aria-live="polite">
                {autosaveStatus === 'saving' && 'Guardando cambios...'}
                {autosaveStatus === 'dirty' &&
                  (hasIncompleteDiagnosis
                    ? 'Seleccione un diagnóstico CIE-10 o quite la fila incompleta.'
                    : 'Cambios pendientes de guardar')}
                {autosaveStatus === 'saved' && `Borrador guardado${lastSavedAt ? ` a las ${lastSavedAt.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}` : ''}`}
                {autosaveStatus === 'error' && 'No fue posible guardar automáticamente. Use Guardar borrador para reintentar.'}
                {autosaveStatus === 'idle' && 'El autoguardado se activará al comenzar a escribir.'}
              </div>
            )}
            <div className="form-grid two">
              <Field label="Plantilla clínica">
                <select
                  disabled={!editable || templates.isLoading}
                  value={form.selectedTemplateId}
                  onChange={(event) => applyTemplate(event.target.value)}
                >
                  <option value="">
                    {templates.isLoading
                      ? 'Cargando plantillas...'
                      : 'Sin plantilla seleccionada'}
                  </option>
                  {completeTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.id === templatePreference.data?.templateId
                        ? 'Predeterminada · '
                        : ''}
                      {template.name}
                      {template.studyType
                        ? ` · ${template.studyType.name}`
                        : ' · General'}
                    </option>
                  ))}
                </select>
                {templatePreference.data ? (
                  <div className="action-row">
                    <small>
                      Sugerida para este tipo: {templatePreference.data.template.name}
                    </small>
                    {Number(form.selectedTemplateId) !== templatePreference.data.templateId ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => applyTemplate(String(templatePreference.data!.templateId))}
                      >
                        Usar sugerida
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </Field>
              <Field
                label="Insertar bloque clínico"
                hint="Inserta el fragmento en la última posición del cursor; no reemplaza el documento."
              >
                <select
                  aria-label="Insertar bloque clínico"
                  disabled={!editable || !clinicalBlocks.length}
                  value=""
                  onChange={(event) => insertClinicalBlock(event.target.value)}
                >
                  <option value="">
                    {clinicalBlocks.length ? 'Seleccione un bloque' : 'No hay bloques disponibles'}
                  </option>
                  {clinicalBlocks.map((block) => (
                    <option key={block.id} value={block.id}>
                      {block.name}
                      {block.studyType ? ` · ${block.studyType.name}` : ' · General'}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Plantilla predeterminada para este tipo"
                hint="Esta preferencia solo organiza la biblioteca; no modifica el informe actual."
              >
                <select
                  aria-label="Plantilla predeterminada para este tipo"
                  value={templatePreference.data?.templateId ?? ''}
                  disabled={!editable || saveTemplatePreference.isPending}
                  onChange={(event) => {
                    const templateId = Number(event.target.value);
                    if (templateId) saveTemplatePreference.mutate(templateId);
                  }}
                >
                  <option value="">Sin predeterminada</option>
                  {completeTemplates
                    .filter(
                      (template) =>
                        template.studyTypeId === null ||
                        template.studyTypeId === study.data.studyType.id,
                    )
                    .map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.studyType
                          ? ` · ${template.studyType.name}`
                          : ' · General'}
                      </option>
                    ))}
                </select>
              </Field>
              <Field
                label="Guardar como plantilla"
                hint="Guarda solo contenido reutilizable. La API rechazará datos o imágenes del paciente actual."
              >
                <div className="template-save-row">
                  <input
                    disabled={!editable}
                    value={templateName}
                    placeholder="Nombre de la plantilla"
                    onChange={(event) => setTemplateName(event.target.value)}
                  />
                  <select aria-label="Clasificación de la plantilla" value={templateStudyTypeId} onChange={(event) => setTemplateStudyTypeId(event.target.value)} disabled={!editable}>
                    <option value="">General</option>
                    <option value={study.data.studyType.id}>{study.data.studyTypeName}</option>
                  </select>
                  <select aria-label="Tipo de contenido reutilizable" value={templateKind} onChange={(event) => setTemplateKind(event.target.value as typeof templateKind)} disabled={!editable}>
                    <option value="PLANTILLA_COMPLETA">Plantilla completa</option>
                    <option value="BLOQUE_CLINICO">Bloque clínico</option>
                  </select>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      !editable ||
                      !templateName.trim() ||
                      !form.richContentText.trim()
                    }
                    busy={saveTemplate.isPending}
                    onClick={() => saveTemplate.mutate()}
                  >
                    Guardar plantilla
                  </Button>
                </div>
              </Field>
            </div>
            {pendingTemplateId !== null && (
              <Notice kind="info">
                <div className="template-apply-choice">
                  <span>
                    El borrador ya tiene contenido. Elija cómo aplicar{' '}
                    <strong>
                      {templates.data?.find(
                        (template) => template.id === pendingTemplateId,
                      )?.name ?? 'la plantilla'}
                    </strong>.
                  </span>
                  <div className="action-row">
                    <Button
                      type="button"
                      variant="warning"
                      onClick={() => resolvePendingTemplate('replace')}
                    >
                      Reemplazar contenido
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => resolvePendingTemplate('insert')}
                    >
                      Insertar al final
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => resolvePendingTemplate('cancel')}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </Notice>
            )}
            <Field
              label="Documento clínico"
              required
              hint="Puede pegar texto o imágenes desde el portapapeles. El contenido se conserva en el borrador y las imágenes se incorporan al informe generado."
            >
              <RichClinicalEditor
                ref={editorRef}
                value={editorHtml}
                editable={editable}
                onChange={syncEditor}
              />
            </Field>
            <Field
              label="Observaciones internas"
              hint="No se incorporan al informe entregado."
            >
              <textarea
                disabled={!editable}
                rows={4}
                value={form.internalObservations}
                onChange={(event) =>
                  setForm({ ...form, internalObservations: event.target.value })
                }
              />
            </Field>
          </div>
        </Card>
        <Card title="Previsualización del borrador">
          <div className="stack">
            <div className="actions-row">
              <Button
                type="button"
                variant="secondary"
                disabled={!editable || !form.richContentText.trim() || previewDraft.isPending}
                onClick={() => previewDraft.mutate()}
              >
                <Eye size={15} />
                {previewDraft.isPending ? 'Generando…' : 'Previsualizar borrador'}
              </Button>
              <span className="muted">
                Genera una vista temporal sin aprobar, bloquear ni crear documentos finales.
              </span>
            </div>
            {previewError ? <Notice kind="error">{previewError}</Notice> : null}
            {previewUrl ? (
              <iframe
                className="pdf-preview-frame"
                src={previewUrl}
                title="Previsualización temporal del borrador"
              />
            ) : null}
          </div>
        </Card>
        <Card title="Diagnósticos CIE10">
          <div className="stack">
            {form.diagnoses.map((diagnosis, index) => (
              <div className="form-grid three" key={index}>
                <div className="cie10-picker-grid-span">
                  <Field label="Buscar diagnóstico CIE-10" required>
                    <Cie10Combobox
                      disabled={!editable}
                      code={diagnosis.cie10Code}
                      description={diagnosis.description}
                      onSelect={(result) => {
                        updateDiagnosis(index, 'cie10Code', result.code);
                        updateDiagnosis(index, 'description', result.description);
                      }}
                    />
                  </Field>
                </div>
                <Field label="Código CIE-10" required>
                  <input
                    disabled={!editable}
                    readOnly
                    required
                    value={diagnosis.cie10Code}
                  />
                </Field>
                <Field label="Descripción" required>
                  <input
                    disabled={!editable}
                    readOnly
                    required
                    value={diagnosis.description}
                  />
                </Field>
                <Field label="Fecha de registro">
                  <input
                    disabled={!editable}
                    type="date"
                    value={diagnosis.registeredAt}
                    onChange={(event) =>
                      updateDiagnosis(index, 'registeredAt', event.target.value)
                    }
                  />
                </Field>
                <Field label="Tipo">
                  <input
                    disabled={!editable}
                    value={diagnosis.diagnosisType}
                    placeholder="Ej. Confirmado nuevo"
                    onChange={(event) =>
                      updateDiagnosis(
                        index,
                        'diagnosisType',
                        event.target.value,
                      )
                    }
                  />
                </Field>
                <Field label="Clasificación">
                  <input
                    disabled={!editable}
                    value={diagnosis.classification}
                    placeholder="Ej. Clínico"
                    onChange={(event) =>
                      updateDiagnosis(
                        index,
                        'classification',
                        event.target.value,
                      )
                    }
                  />
                </Field>
                <label>
                  <input
                    disabled={!editable}
                    type="checkbox"
                    checked={diagnosis.isPrimary}
                    onChange={(event) =>
                      updateDiagnosis(index, 'isPrimary', event.target.checked)
                    }
                  />{' '}
                  Diagnóstico principal
                </label>
                {editable && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => removeDiagnosis(index)}
                  >
                    Quitar diagnóstico
                  </Button>
                )}
              </div>
            ))}
            {editable && (
              <Button type="button" variant="secondary" onClick={addDiagnosis}>
                Agregar diagnóstico
              </Button>
            )}
            {!form.diagnoses.length && (
              <small>
                Puede registrar diagnósticos estructurados cuando correspondan
                al estudio.
              </small>
            )}
          </div>
        </Card>
        <div className="sticky-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/studies/${id}`)}
          >
            Volver
          </Button>
          {canReturnToTechnique && (
            <>
              <Field
                label="Observación para Técnica"
                hint="Indique qué información o evidencia debe revisarse."
              >
                <textarea
                  rows={3}
                  value={technicalReturnComment}
                  onChange={(event) =>
                    setTechnicalReturnComment(event.target.value)
                  }
                />
              </Field>
              <Button
                type="button"
                variant="warning"
                disabled={technicalReturnComment.trim().length < 5}
                busy={returnToTechnique.isPending}
                onClick={() => returnToTechnique.mutate()}
              >
                Devolver a Técnica
              </Button>
            </>
          )}
          {pendingDecision && assignedDoctor ? (
            <>
              <Field
                label="Justificación de devolución"
                hint="Obligatoria si devuelve el informe."
              >
                <textarea
                  rows={3}
                  value={returnComment}
                  onChange={(event) => setReturnComment(event.target.value)}
                />
              </Field>
              <Button
                type="button"
                variant="warning"
                disabled={!returnComment.trim()}
                busy={decision.isPending}
                onClick={() => decision.mutate()}
              >
                Devolver para corrección
              </Button>
              <Button
                type="button"
                busy={finalizeReport.isPending}
                onClick={() => finalizeReport.mutate()}
              >
                Finalizar, aprobar y generar
              </Button>
            </>
          ) : canCreatePostApprovalCorrection ? (
            <Button
              type="button"
              variant="warning"
              busy={correction.isPending}
              onClick={() => correction.mutate()}
            >
              Crear versión de corrección
            </Button>
          ) : !locked ? (
            <>
              <Button
                type="submit"
                disabled={!editable || hasIncompleteDiagnosis}
                busy={saveDraft.isPending}
              >
                Guardar borrador
              </Button>
              <Button
                type="button"
                disabled={!canSubmit}
                busy={finalizeReport.isPending}
                onClick={() => finalizeReport.mutate()}
              >
                Finalizar, aprobar y generar
              </Button>
            </>
          ) : null}
        </div>
      </form>
    </>
  );
}
